import React from "react";
import { Redirect, Route, RouteProps } from "react-router-dom";
import { compose } from "redux";
import { connect } from "react-redux";
import { Forbidden, IFrameNotSupported, NotFound } from "../Pages/ErrorPage";
import {
  hasPermission as hasPermissionSelector,
  isAuthenticated as isAuthenticatedSelector,
  selectIsCuvamaUser
} from "../store/reducers";
import isInIFrame from "../utils/is-in-iframe";
import { useFeature } from "../components/FeaturesProvider";

interface CuvamaRouteOwnProps extends Omit<RouteProps, "component"> {
  component: React.ComponentType<any>;
  supportsIFrame?: boolean;
  requiresAuthentication?: boolean;
  requiresPermission?: string;
  requiresFeature?: string;
  requiresCuvamaUser?: boolean;
}

interface CuvamaRouteStateProps {
  isAuthenticated: boolean;
  isCuvamaUser: boolean;
  hasPermission: boolean;
}

type CuvamaRouteProps = CuvamaRouteOwnProps & CuvamaRouteStateProps;

function CuvamaRoute({
  component: Component,
  supportsIFrame = false,
  requiresAuthentication = false,
  requiresFeature,
  requiresCuvamaUser = false,
  isCuvamaUser,
  hasPermission,
  path,
  isAuthenticated,
  ...rest
}: CuvamaRouteProps) {
  const hasFeature = requiresFeature ? useFeature(requiresFeature) : true;

  return (
    <Route
      {...rest}
      render={(props) => {
        // Check iframe support
        if (!supportsIFrame && isInIFrame()) {
          return <IFrameNotSupported />;
        }

        // Check all conditions
        const featureCondition = !requiresFeature || hasFeature;
        const authCondition =
          !requiresAuthentication || (isAuthenticated && hasPermission);
        const cuvamaUserCondition = !requiresCuvamaUser || isCuvamaUser;

        // All conditions satisfied - render component
        if (featureCondition && authCondition && cuvamaUserCondition) {
          return <Component {...props} />;
        }

        // Feature not available
        if (requiresFeature && !hasFeature) {
          return <NotFound />;
        }

        // Cuvama user required but not satisfied
        if (requiresCuvamaUser && !isCuvamaUser) {
          return <NotFound />;
        }

        // Authenticated but missing permissions
        if (isAuthenticated && !hasPermission) {
          return <Forbidden />;
        }

        // Not authenticated - redirect to login
        return (
          <Redirect
            to={{
              pathname: "/login",
              state: {
                from: props.location,
                error: "You must login to see this page."
              }
            }}
          />
        );
      }}
    />
  );
}

const mapStateToProps = (
  state: any,
  props: CuvamaRouteOwnProps
): CuvamaRouteStateProps => ({
  isAuthenticated: isAuthenticatedSelector(state),
  isCuvamaUser: selectIsCuvamaUser(state),
  hasPermission:
    !props.requiresPermission ||
    hasPermissionSelector(state, props.requiresPermission)
});

export default compose(connect(mapStateToProps))(CuvamaRoute);
